import React, { useContext, useEffect, useState } from 'react'
import { useHistory, useLocation, Redirect } from 'react-router-dom'
import { NavLink } from 'react-router-dom'
import { CONFIRMED, PALO_BLUE, PATHS, SUBMITTED, BLOCKED } from '../../constants'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import LogoutIcon from '@mui/icons-material/Logout'
import SettingsIcon from '@mui/icons-material/Settings'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import SupervisedUserCircleOutlinedIcon from '@mui/icons-material/SupervisedUserCircleOutlined';
import { Typography } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { authenticationService, SELECTED_STORE_CHANGED_EVENT } from '../../Utilities/authenticationService'
import { useStickyState } from '../../Utilities/useStickyState'
import {
    AccountCircleOutlined,
    EventNoteOutlined,
    HomeOutlined,
    AutoGraphOutlined,
    SendOutlined,
    EditOutlined,
    SearchOutlined,
    CalendarToday,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    ExpandMore,
    ExpandLess
} from '@mui/icons-material'
import { SwipeableDrawer, Drawer, Divider, List, BottomNavigation, BottomNavigationAction, Snackbar, Alert, Collapse } from '@mui/material'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import { TourProvider } from '../../contexts/TourContext'
import TourOverlay from '../Tour/TourOverlay'
import ButtonWithConfirmPrompt from '../ButtonWithConfirmPrompt'
import { isMobile } from '../../Utilities/isMobile'
import moment from 'moment'

const drawerWidth = 240

export const theme = createTheme({
    palette: {
        paloBlue: {
            main: PALO_BLUE,
            contrastText: '#fff'
        },
        success: {
            main: '#4CAF50',
            contrastText: '#fff'
        },
        submitted: {
            main: SUBMITTED,
            contrastText: '#fff'
        },
        confirmed: {
            main: CONFIRMED,
            contrastText: '#fff'
        },
        blocked: {
            main: BLOCKED,
            contrastText: '#fff'
        }
    },
    components: {
        MuiAutocomplete: {
            defaultProps: {
                noOptionsText: '該当なし'
            }
        }
    }
})

const DrawerItem = ({ toggleDrawer, path, text, icon, isExternal = false, isHighlighted = false }) => {
    if (isExternal) {
        return (
            <MenuItem 
                onClick={() => {
                    toggleDrawer(false);
                    window.location.href = 'https://ringerhutschedule.com/home';
                }}
                sx={isHighlighted ? {
                    backgroundColor: '#FF9800',
                    color: 'white',
                    '&:hover': {
                        backgroundColor: '#F57C00',
                    },
                    '& .MuiListItemIcon-root': {
                        color: 'white'
                    },
                    '& .MuiListItemText-primary': {
                        fontWeight: 'bold'
                    }
                } : {}}
            >
                <ListItemIcon sx={{ mr: '1rem' }}>{icon}</ListItemIcon>
                <ListItemText primary={text} />
            </MenuItem>
        )
    }
    return (
        <NavLink to={path} aria-label={text} onClick={() => toggleDrawer(false)}>
            <MenuItem>
                <ListItemIcon sx={{ mr: '1rem' }}>{icon}</ListItemIcon>
                <ListItemText primary={text} />
            </MenuItem>
        </NavLink>
    )
}

const Layout = ({ children, testMode=false }) => {
    const history = useHistory()
    const location = useLocation()
    const [open, setOpen] = useState(false)
    const [desktopMenuOpen, setDesktopMenuOpen] = useStickyState(true, 'frederickDesktopMenuOpen')
    const [adminMenuOpen, setAdminMenuOpen] = useState(false)
    const { isManager, canEditShift, setUserEmployments, isAdmin, resetAppsStates, isManagerFor = {} } = useContext(EmploymentsContext)
    const [value, setValue] = React.useState(0);
    const [selectedEmploymentId, setSelectedEmploymentId] = useState(
        () => parseInt(localStorage.getItem('selected_emp') || 0, 10)
    )
    const isLoggedIn = localStorage.getItem('currentUser') !== null
    // Get calendarYearMonth from localStorage with fallback to current month if null
    const storedYearMonth = localStorage.getItem('frederickSelectedYearMonth')
    const calendarYearMonth = storedYearMonth ? JSON.parse(storedYearMonth) : moment().format('YYYY-MM')
    const [snackbar, setSnackbar] = React.useState({}) // { message: string, severity: string success|error|warning|info }
    const currentUser = authenticationService.currentUserValue
    const isCompanyAdmin = currentUser?.company_admin || false
    const isMasterAdmin = isAdmin && currentUser?.is_admin // TODO: Check for master admin email
    const viewOnly = currentUser?.view_only
    // Prefer live localStorage, fall back to React state used for re-renders after store changes.
    const resolveSelectedEmploymentId = () =>
        parseInt(localStorage.getItem('selected_emp') || selectedEmploymentId || 0, 10)
    // Overall evaluation graph is store-scoped: manager for the selected store, or admin (users.is_admin).
    const canViewOverallEvaluationGraph =
        isAdmin || Boolean(isManagerFor[resolveSelectedEmploymentId()])
    
    // Auto-expand admin menu if on admin page
    useEffect(() => {
        if ((isAdmin || isCompanyAdmin) && (location.pathname.startsWith('/adminSettings'))) {
            setAdminMenuOpen(true)
        }
    }, [location.pathname, isAdmin, isCompanyAdmin])

    useEffect(() => {
        const handleStoreChanged = e => {
            const newId = parseInt(e.detail?.employmentId ?? (localStorage.getItem('selected_emp') || 0), 10)
            setSelectedEmploymentId(newId)
        }
        window.addEventListener(SELECTED_STORE_CHANGED_EVENT, handleStoreChanged)
        return () => window.removeEventListener(SELECTED_STORE_CHANGED_EVENT, handleStoreChanged)
    }, [])

    // Re-read selected_emp on navigation so visibility stays aligned even if localStorage was
    // updated without SELECTED_STORE_CHANGED_EVENT (e.g. employment removal fallbacks).
    useEffect(() => {
        const fromStorage = parseInt(localStorage.getItem('selected_emp') || 0, 10)
        setSelectedEmploymentId(fromStorage)
    }, [location.pathname])

    const openOverallEvaluationGraph = () => {
        const employmentId = resolveSelectedEmploymentId()
        if (employmentId !== selectedEmploymentId) {
            setSelectedEmploymentId(employmentId)
        }
        if (!(isAdmin || isManagerFor[employmentId])) {
            return
        }
        history.push(
            PATHS.feedbackManagement
                .replace(':employmentId', employmentId)
                .replace(':calendarYearMonth', calendarYearMonth)
        )
    }

    const toggleDrawer = action => {
        setOpen(action)
        setDesktopMenuOpen(action)
    }

    const handleLogout = () => {
        setUserEmployments([])
        resetAppsStates()
        localStorage.clear()
        authenticationService.logout()
        history.push(PATHS.login)
    }

    const shouldBeHighlighted = (path) => {
        return location.pathname.startsWith(path)
    }
    
    const toolbar = (
        <>
            <Box sx={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <Box sx={{ width: drawerWidth, cursor: 'pointer' }} onClick={() => history.push(PATHS.home)}>
                    <img src="/company-logo.png" alt="company-logo" style={{ width: drawerWidth, height: 'auto' }} />
                    {process.env.REACT_APP_VERSION && <div style={{ fontSize: '10px', color: 'lightgrey', position: 'absolute', marginLeft: "3.25rem", marginTop: "-1rem" }}>Ver. {process.env.REACT_APP_VERSION}</div>}
                    {currentUser?.company && !currentUser.company.default_company && (
                        <Typography
                            variant="body2"
                            sx={{
                                padding: '0.25rem 0.5rem',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                            }}
                        >
                            {currentUser.company.company_name}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    sx={{ mr: 2 }}
                    onClick={() => toggleDrawer(true)}
                >
                    <MenuIcon />
                </IconButton>
            </Box>
            <SwipeableDrawer
                id="drawer"
                anchor="left"
                open={open}
                onClose={() => toggleDrawer(false)}
                onOpen={() => toggleDrawer(true)}
            >
                <List>
                    <DrawerItem toggleDrawer={toggleDrawer} path={PATHS.home} icon={<HomeOutlined />} text="ホーム" />
                </List>

                {!viewOnly && (<>
                    <Divider orientation="horizontal" flexItem />
                    <List>
                        {(isManager || canEditShift) && (
                            <DrawerItem
                                toggleDrawer={toggleDrawer}
                                path={PATHS.shiftManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth).replace(':tabValue', 'create')}
                                icon={<EditOutlined />}
                                text="シフト表を作成する"
                            />
                        )}
                        <DrawerItem
                            toggleDrawer={toggleDrawer}
                            path={PATHS.shiftManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth).replace(':tabValue', 'check')}
                            icon={<EventNoteOutlined />}
                            text="シフトを確認する"
                        />
                        <DrawerItem
                            toggleDrawer={toggleDrawer}
                            path={PATHS.shiftManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth).replace(':tabValue', 'submit')}
                            icon={<SendOutlined />}
                            text="希望シフトを提出する"
                        />
                    </List>
                </>)}
                <Divider orientation="horizontal" flexItem />
                <List>
                    <DrawerItem
                        toggleDrawer={toggleDrawer}
                        path={PATHS.userSettings}
                        icon={<AccountCircleOutlined />}
                        text="あなたのアカウント"
                    />
                    {isManager && (
                        <DrawerItem
                            toggleDrawer={toggleDrawer}
                            path={PATHS.managerSettingsUsers}
                            icon={<SettingsIcon />}
                            text="設定"
                        />
                    )}
                    <ButtonWithConfirmPrompt
                        component={
                            <MenuItem aria-label="logout">
                                <ListItemIcon sx={{ mr: '1rem' }}>
                                    <LogoutIcon />
                                </ListItemIcon>
                                <ListItemText primary="ログアウト" />
                            </MenuItem>
                        }
                        title="ログアウト"
                        content="ログアウトしてよろしいですか？"
                        confirmText="ログアウト"
                        handleConfirm={() => {
                            toggleDrawer(false)
                            handleLogout()
                        }}
                    />
                </List>
            </SwipeableDrawer>
        </>
    )

    const desktopMenu = (
        <>
            <Drawer variant="permanent" open={desktopMenuOpen}>   
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100vh',
                    width: desktopMenuOpen ? '100%' : '3.5rem'
                }}>
                    <List>
                        <Box sx={{ cursor: 'pointer' }} onClick={() => history.push(PATHS.home)}>
                            {desktopMenuOpen ? (
                                <>
                                    <img src="/company-logo.png" alt="company-logo" style={{ margin: '.5rem', width: 230, height: 'auto' }} />
                                    {process.env.REACT_APP_VERSION && <div style={{ fontSize: '10px', color: 'lightgrey', position: 'absolute', marginLeft: "3.5rem", marginTop: "-1.5rem" }}>Ver. {process.env.REACT_APP_VERSION}</div>}
                                    {currentUser?.company && !currentUser.company.default_company && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                margin: '0 0.5rem 0.5rem 0.5rem',
                                                textAlign: 'center',
                                                fontWeight: 'bold',
                                                fontSize: '0.875rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={currentUser.company.company_name || ''}
                                        >
                                            {(currentUser.company.company_name || '').length > 15 
                                                ? `${(currentUser.company.company_name || '').substring(0, 15)}...`
                                                : (currentUser.company.company_name || '')}
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <>
                                    <img src="/company-logo-collasped.png" alt="company-logo" style={{ margin: '.5rem', width: '2.5rem', height: 'auto' }} />
                                    {currentUser?.company && !currentUser.company.default_company && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                margin: '0 0.25rem 0.5rem 0.25rem',
                                                textAlign: 'center',
                                                fontWeight: 'bold',
                                                fontSize: '0.65rem',
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={currentUser.company.company_name || ''}
                                        >
                                            {currentUser.company.company_name || ''}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>

                        <MenuItem
                            onClick={() => history.push(PATHS.home)}
                            sx={{ fontSize: '1rem', backgroundColor: shouldBeHighlighted(PATHS.home) ? '#e3f2fd' : 'transparent' }}
                        >
                            <HomeOutlined sx={{ mr: '1rem' }} />
                            {desktopMenuOpen && <Typography>ホーム</Typography>}
                        </MenuItem>
                        <MenuItem
                            onClick={() => history.push(PATHS.shiftManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth).replace(':tabValue', 'check'))}
                            sx={{ fontSize: '1rem', backgroundColor: shouldBeHighlighted(PATHS.shiftManagement.split(':')[0]) ? '#e3f2fd' : 'transparent' }}
                        >
                            <EventNoteOutlined sx={{ mr: '1rem' }} />
                            {desktopMenuOpen && <Typography>シフト管理</Typography>}
                        </MenuItem>
                        {isManager && (<>
                            <MenuItem onClick={() => history.push(PATHS.managerSettingsUsers)} sx={{ fontSize: '1rem', backgroundColor: shouldBeHighlighted(PATHS.managerSettingsUsers) ? '#e3f2fd' : 'transparent' }}>
                                <SupervisedUserCircleOutlinedIcon sx={{ mr: '1rem' }}/>
                                {desktopMenuOpen && <Typography>ユーザー・店舗情報</Typography>}
                            </MenuItem>
                        </>)}
                        {canViewOverallEvaluationGraph && (
                            <MenuItem
                                onClick={openOverallEvaluationGraph}
                                sx={{ fontSize: '1rem', backgroundColor: shouldBeHighlighted(PATHS.feedbackManagement.split(':')[0]) ? '#e3f2fd' : 'transparent' }}
                            >
                                <AutoGraphOutlined sx={{ mr: '1rem' }} />
                                {desktopMenuOpen && <Typography>総合評価グラフ</Typography>}
                            </MenuItem>
                        )}
                        {(isAdmin || isCompanyAdmin) && (
                            <>
                                <MenuItem 
                                    onClick={() => setAdminMenuOpen(!adminMenuOpen)} 
                                    sx={{ fontSize: '1rem' }}
                                >
                                    <VpnKeyOutlinedIcon sx={{ mr: '1rem' }}/>
                                    {desktopMenuOpen && <Typography>管理者</Typography>}
                                    {desktopMenuOpen && (adminMenuOpen ? <ExpandLess sx={{ ml: 'auto' }} /> : <ExpandMore sx={{ ml: 'auto' }} />)}
                                </MenuItem>
                                <Collapse in={adminMenuOpen && desktopMenuOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        <MenuItem 
                                            onClick={() => {
                                                history.push(PATHS.adminUserManagements)
                                                setAdminMenuOpen(false)
                                            }} 
                                            sx={{ pl: 4, fontSize: '1rem', backgroundColor: shouldBeHighlighted(PATHS.adminUserManagements) ? '#e3f2fd' : 'transparent' }}
                                        >
                                            <Typography>ユーザー管理</Typography>
                                        </MenuItem>
                                        <MenuItem 
                                            onClick={() => {
                                                history.push(PATHS.adminStoreManagements)
                                                setAdminMenuOpen(false)
                                            }} 
                                            sx={{ pl: 4, fontSize: '1rem', backgroundColor: shouldBeHighlighted(PATHS.adminStoreManagements) ? '#e3f2fd' : 'transparent' }}
                                        >
                                            <Typography>店舗管理</Typography>
                                        </MenuItem>
                                        {isMasterAdmin && (<>
                                        <MenuItem 
                                            onClick={() => {
                                                history.push(PATHS.adminHLG)
                                                setAdminMenuOpen(false)
                                            }} 
                                            sx={{ pl: 4, fontSize: '1rem', backgroundColor: shouldBeHighlighted(PATHS.adminHLG) ? '#e3f2fd' : 'transparent' }}
                                        >
                                            <Typography>HLG管理</Typography>
                                        </MenuItem>
                                        
                                            <MenuItem 
                                                onClick={() => {
                                                    history.push(PATHS.adminCompanyManagements)
                                                    setAdminMenuOpen(false)
                                                }} 
                                                sx={{ pl: 4, fontSize: '1rem', backgroundColor: shouldBeHighlighted(PATHS.adminCompanyManagements) ? '#e3f2fd' : 'transparent' }}
                                            >
                                                <Typography>会社管理</Typography>
                                            </MenuItem>
                                        </>)}
                                    </List>
                                </Collapse>
                            </>
                        )}
                    </List>
                    <List sx={{ pb: 0 }}>
                        <MenuItem
                            onClick={() => history.push(PATHS.userSettings)}
                            sx={{ fontSize: '1rem' }}
                        >
                            <AccountCircleOutlined sx={{ mr: '1rem' }} />
                            {desktopMenuOpen && <Typography>あなたのアカウント</Typography>}
                        </MenuItem>
                        <ButtonWithConfirmPrompt
                            component={
                                <MenuItem aria-label="logout">
                                    <LogoutIcon sx={{ mr: '1rem' }}/>
                                    {desktopMenuOpen && <Typography>ログアウト</Typography>}
                                </MenuItem>
                            }
                            title="ログアウト"
                            content="ログアウトしてよろしいですか？"
                            confirmText="ログアウト"
                            handleConfirm={() => {
                                toggleDrawer(false)
                                handleLogout()
                            }}
                        />
                        <Box sx={{ backgroundColor: '#1976D2', mb: 0, display: 'flex', justifyContent: 'space-evenly' }}>
                            {desktopMenuOpen ?
                                <IconButton
                                    onClick={() => setDesktopMenuOpen(false)}
                                >
                                    <KeyboardArrowLeft sx={{color: 'white'}}/>
                                </IconButton>
                                :
                                <IconButton
                                    onClick={() => setDesktopMenuOpen(true)}
                                >
                                    <KeyboardArrowRight sx={{color: 'white'}}/>
                                </IconButton>
                            }
                        </Box>
                    </List>
                </Box>
            </Drawer>
        </>
    )
    
    const mobileBottomNavigation = <>
        {localStorage.getItem('selected_emp') && 
            <Box sx={{ width: '100vw', position: 'fixed', bottom: 0, zIndex: 1000, boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.1)' }}>
                <BottomNavigation
                    showLabels
                    value={value}
                    onChange={(event, newValue) => {
                        history.push(newValue)
                        setValue(newValue);
                    }}
                    sx={{ width: '100%', position: 'sticky', bottom: 0, backgroundColor: 'white'}}
                >
                    <BottomNavigationAction label="ホーム" icon={<HomeOutlined />} value={PATHS.home}/>
                    {!viewOnly && <BottomNavigationAction label="シフト" icon={<CalendarToday />} value={PATHS.shiftManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth).replace(':tabValue', 'check')}/>}
                    {(isManager || isAdmin) && <BottomNavigationAction label="検索" icon={<SearchOutlined />} value={PATHS.managerSettingsUsers}/>}
                    {/* {isAdmin && <BottomNavigationAction label="管理者" icon={<VpnKeyOutlined />} value={PATHS.adminUserManagements}/>} */}
                    <BottomNavigationAction
                        label="アカウント"
                        icon={<AccountCircleOutlined />}
                        sx={{
                            '& .MuiBottomNavigationAction-label': {
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                            }
                        }}
                        value={PATHS.userSettings}
                    />
                </BottomNavigation>
            </Box>
        }
    </>

    if (!currentUser && !testMode) {
        // not logged in so redirect to login page with the return url
        return (
            <Redirect
                to={{
                    pathname: PATHS.login,
                }}
            />
        )
    }
    
    return (
        <TourProvider>
            <ThemeProvider theme={theme}>
                <Box sx={{ flexGrow: 1 }}>
                        {!isMobile() ? (
                            localStorage.getItem('currentUser') == null ? null : (
                                desktopMenu
                            )
                        ) : (
                            <AppBar position="static" color="">
                                {localStorage.getItem('currentUser') == null ? null : toolbar}
                                {mobileBottomNavigation}
                            </AppBar>
                        )}
                        <div style={{marginLeft: isMobile() ? 0 : isLoggedIn ? desktopMenuOpen ? '250px' : '3rem' : 0}}>
                            <Snackbar
                                open={(snackbar?.message?.length || 0) > 0}
                                autoHideDuration={10000}
                                onClose={() => setSnackbar({})}
                                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            >
                                <Alert severity={snackbar.severity || 'success'} onClose={() => setSnackbar({})}>
                                    {snackbar.message}
                                </Alert>
                            </Snackbar>
                            {children}
                            <TourOverlay />
                        </div>
                </Box>
            </ThemeProvider>
        </TourProvider>
    )
}

export default Layout
